import { DownloadOutlined } from "@ant-design/icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Divider } from "antd";
import { useForm } from "react-hook-form";
import { object, string } from "yup";
import { ProctorPersonalInformation } from "../../../interfaces/proctors.interface";
import CustomCard from "../../shared/CustomCard/CustomCard";
import { useRole } from "../../../hooks/user-roles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CBInput } from "../../shared";
import { updateUser } from "../../../services/users.service";
import Message from "../../shared/Message/Message";
import { AxiosError } from "axios";
import ProctorProfileTable from "./proctor-profile-table";

const ProctorProfile = () => {
  const queryClient = useQueryClient();
  const { user } = useRole();
  const mutationOnError = (err: AxiosError) => {
    if (err.code === "ERR_BAD_REQUEST") {
      Message().error("The information provided is not right");
    } else {
      Message().error(err.message);
    }
  };
  const { control, handleSubmit } = useForm<ProctorPersonalInformation>({
    mode: "onBlur",
    resolver: yupResolver(
      object({
        firstName: string().required(),
        lastName: string().required(),
        email: string().email().required(),
        phone: string().required(),
        association: string().required(),
      }),
    ),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      association: "",
    },
    values: user,
  });

  const updateProctor = useMutation({
    mutationFn: (formInfo: ProctorPersonalInformation) => {
      const id = user?.id ?? 0;
      const data = { id, ...formInfo };
      return updateUser(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`isLoggedIn`],
      });
      Message().success(
        "Your proctor contact details have been updated. You can now proceed to manage your student(s)",
      );
    },
    onError: mutationOnError,
  });

  const submitForm = (formInfo: ProctorPersonalInformation) => {
    updateProctor.mutate(formInfo);
  };

  return (
    <div className="p-8">
      <div className="max-w-4xl w-3/4 m-auto">
        <CustomCard
          title="Proctor Contact Details"
          extra={
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleSubmit(submitForm)}
            >
              Update and save
            </Button>
          }
        >
          <form onSubmit={handleSubmit(submitForm)}>
            <div className="flex gap-10 p-5">
              <CBInput
                className="flex-1"
                control={control}
                label="First Name"
                name="firstName"
              />
              <CBInput
                className="flex-1"
                control={control}
                label="Last Name"
                name="lastName"
              />
            </div>
            <div className="flex gap-10 p-5">
              <CBInput
                className="flex-1"
                control={control}
                type="email"
                label="Email"
                name="email"
              />
              <CBInput
                className="flex-1"
                control={control}
                label="Phone"
                name="phone"
              />
            </div>
            <CBInput
              className="flex-1"
              control={control}
              label="I am a member of this professional organization (i.e.P.Eng.APEGBC.ASTTBC)"
              name="association"
            />
          </form>
          <Divider orientation="left">Proctor Requirements</Divider>
          <ul>
            <li>
              Have standing in a professional association(e.g. EGBC,ASTTBC)
            </li>
            <li>Not be related to the student</li>
            <li>
              Not be in any relationship to the student to avoid conflict of
              interest.
            </li>
            <li>
              Proctor will need to fill their information and accept role.
            </li>
            <li>
              Proctor will conduct exam and submit it using “Exam submission
              code from student”.
            </li>
          </ul>
        </CustomCard>
      </div>
      <ProctorProfileTable />
    </div>
  );
};

export default ProctorProfile;
